import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Search, X, RotateCcw } from 'lucide-react';
import type { AlertSeverity, AlertStatus } from '@/types/compliance';
import { alertSeverityLabels, alertStatusLabels } from '@/types/compliance';

export interface AlertFiltersState {
  search: string;
  severity: AlertSeverity | 'all';
  status: AlertStatus | 'all';
}

interface AlertFiltersProps {
  filters: AlertFiltersState;
  onChange: (filters: AlertFiltersState) => void;
}

const defaultFilters: AlertFiltersState = {
  search: '',
  severity: 'all',
  status: 'all',
};

export function AlertFilters({ filters, onChange }: AlertFiltersProps) {
  const activeFiltersCount = [
    filters.severity !== 'all',
    filters.status !== 'all',
  ].filter(Boolean).length;

  const handleReset = () => {
    onChange(defaultFilters);
  };

  const updateFilter = <K extends keyof AlertFiltersState>(
    key: K,
    value: AlertFiltersState[K]
  ) => {
    onChange({ ...filters, [key]: value });
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar alertas..."
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            className="pl-9"
          />
        </div>

        {/* Severity */}
        <Select
          value={filters.severity}
          onValueChange={(v) => updateFilter('severity', v as AlertSeverity | 'all')}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Severidad" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {Object.entries(alertSeverityLabels).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Status */}
        <Select
          value={filters.status}
          onValueChange={(v) => updateFilter('status', v as AlertStatus | 'all')}
        >
          <SelectTrigger className="w-[150px]">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            {Object.entries(alertStatusLabels).map(([key, label]) => (
              <SelectItem key={key} value={key}>
                {label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {activeFiltersCount > 0 && (
          <Button variant="ghost" size="sm" onClick={handleReset} className="gap-1">
            <RotateCcw className="h-3 w-3" />
            Limpiar
          </Button>
        )}
      </div>

      {/* Active filter badges */}
      {activeFiltersCount > 0 && (
        <div className="flex flex-wrap gap-2">
          {filters.severity !== 'all' && (
            <Badge variant="secondary" className="gap-1">
              Severidad: {alertSeverityLabels[filters.severity]}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => updateFilter('severity', 'all')}
              />
            </Badge>
          )}
          {filters.status !== 'all' && (
            <Badge variant="secondary" className="gap-1">
              Estado: {alertStatusLabels[filters.status]}
              <X
                className="h-3 w-3 cursor-pointer"
                onClick={() => updateFilter('status', 'all')}
              />
            </Badge>
          )}
        </div>
      )}
    </div>
  );
}
